import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import {
  PROPERTY_CALC_KEYS,
  type CalculationAssumptions,
  type ExternalSourceInfo,
  type PropertyState,
} from '@brimo/shared';
import { Banner, Button, Icon, Pill, Progress, Select, Sheet, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useKnownUsers, type KnownUser } from '../../contacts/users';
import { confirmBaseData, updateProperty } from '../api';
import {
  isManagedField,
  isUndeliveredManagedField,
  syncedHint,
  undeliveredHint,
  withoutManagedFields,
} from '../availability';
import { formatNumber } from '../format';
import type { PropertyRecord } from '../types';
import {
  baseDataBody,
  baseDataDraftOf,
  draftCompleteness,
  progressText,
  type BaseDataDraft,
} from './baseDataDraft';
import { useOrganizationHints } from './CalcDefaultsCard';
import { CalcDefaultsFields } from './CalcDefaultsFields';
import {
  CALC_GROUPS,
  calcDefaultsBody,
  calcDefaultsDraftOf,
  type CalcDefaultsDraft,
  type CalcDefaultsKey,
} from './calcDefaultsDraft';
import { STATE_OPTIONS, automaticRate, stateHint } from './PropertyEditDialog';
import styles from './BaseDataOnboarding.module.css';
import dialogStyles from './dialogs.module.css';

/**
 * „Stammdaten" of the property record (Nico feedback FB-004, owner decision 29.10.2026, D-315–D-319): the status
 * line in the record head and the sheet that shows only the base-data fields. Fields TeamProQ delivered stay
 * read-only with the lock; managed fields TeamProQ left empty are maintained here. The last group „Kalkulation"
 * (D-362) holds the eight „Objekt-Vorgaben" (the same field group as the sheet „Objekt-Vorgaben").
 */

export interface BaseDataStatusProps {
  record: PropertyRecord;
  /** Opens the sheet; null when the user cannot edit the property. */
  onComplete: (() => void) | null;
  onConfirm: (() => void) | null;
  confirming: boolean;
}

/** Banner „Stammdaten unvollständig · 8/11", the muted „Stammdaten vollständig" line or the confirmed pill. */
export function BaseDataStatusLine({ record, onComplete, onConfirm, confirming }: BaseDataStatusProps) {
  const base = record.baseData;
  if (base.status !== 'complete') {
    return (
      <Banner
        tone="warning"
        withIcon
        title={`Stammdaten unvollständig · ${base.doneCount}/${base.totalCount}`}
        actions={
          onComplete ? (
            <Button size="small" onClick={onComplete}>
              Stammdaten vervollständigen
            </Button>
          ) : undefined
        }
      />
    );
  }
  if (base.confirmedLabel) {
    return (
      <div className={styles.statusLine}>
        <Pill variant="success" dot>
          {base.confirmedLabel}
        </Pill>
      </div>
    );
  }
  return (
    <div className={styles.statusLine}>
      <span className={styles.statusMuted}>Stammdaten vollständig</span>
      {onConfirm ? (
        <Button variant="secondary" size="small" loading={confirming} onClick={onConfirm}>
          Bestätigen
        </Button>
      ) : null}
    </div>
  );
}

export interface BaseDataFormProps {
  record: PropertyRecord;
  draft: BaseDataDraft;
  calcDraft: CalcDefaultsDraft;
  fields: Record<string, string>;
  users: readonly KnownUser[];
  /** The organization's assumptions for the hints of group „Kalkulation"; null without read access. */
  org: CalculationAssumptions | null;
  onChange: <K extends keyof BaseDataDraft>(key: K, value: BaseDataDraft[K]) => void;
  onCalcChange: (key: CalcDefaultsKey, value: string) => void;
}

/** The base-data group „Kalkulation": the eight calculation fields in the order of the „Objekt-Vorgaben". */
const CALC_BASE_DATA_KEYS = CALC_GROUPS.flatMap((group) => group.keys).filter((key) =>
  (PROPERTY_CALC_KEYS as readonly string[]).includes(key),
);

type FieldProps = { readOnly?: true; hint?: string; labelEnd?: ReactNode };

function fieldPropsOf(external: ExternalSourceInfo, field: string, hint?: string): FieldProps {
  if (external && isManagedField(external, field)) {
    return {
      readOnly: true,
      hint: syncedHint(external),
      labelEnd: <Icon icon={Lock} size={16} aria-label="Gesperrt" />,
    };
  }
  if (external && isUndeliveredManagedField(external, field)) return { hint: undeliveredHint(external) };
  return hint === undefined ? {} : { hint };
}

/** The four groups of the sheet with the progress bar; rendered inside the sheet (and by the unit test). */
export function BaseDataForm({
  record,
  draft,
  calcDraft,
  fields,
  users,
  org,
  onChange,
  onCalcChange,
}: BaseDataFormProps) {
  const external = record.external;
  const props = (field: string, hint?: string) => fieldPropsOf(external, field, hint);
  const completeness = draftCompleteness(record, draft, calcDraft);
  const progress = progressText(completeness);
  return (
    <div className={dialogStyles.form}>
      <div className={styles.progress}>
        <span className={styles.progressText}>{progress}</span>
        <Progress
          label={progress}
          value={completeness.totalCount === 0 ? 0 : (completeness.doneCount / completeness.totalCount) * 100}
        />
      </div>

      <div className={dialogStyles.group}>
        <div className={dialogStyles.groupTitle}>Gebäude</div>
        <div className={dialogStyles.grid2}>
          <TextField
            label="Baujahr von"
            required
            inputMode="numeric"
            value={draft.yearBuiltFrom}
            error={fields['yearBuiltFrom']}
            {...props('yearBuiltFrom')}
            onChange={(event) => onChange('yearBuiltFrom', event.target.value)}
          />
          <TextField
            label="Baujahr bis"
            optional
            inputMode="numeric"
            value={draft.yearBuiltUntil}
            error={fields['yearBuiltUntil']}
            {...props('yearBuiltUntil')}
            onChange={(event) => onChange('yearBuiltUntil', event.target.value)}
          />
          <TextField
            label="Sanierungsjahr"
            optional
            inputMode="numeric"
            value={draft.yearRenovated}
            error={fields['yearRenovated']}
            {...props('yearRenovated')}
            onChange={(event) => onChange('yearRenovated', event.target.value)}
          />
          <TextField
            label="Wohnfläche gesamt"
            required
            inputMode="decimal"
            trailing={<span className={dialogStyles.suffix}>m²</span>}
            value={draft.livingAreaTotalSqm}
            error={fields['livingAreaTotalSqm']}
            {...props('livingAreaTotalSqm')}
            onChange={(event) => onChange('livingAreaTotalSqm', event.target.value)}
          />
          <TextField
            label="Energieausweis"
            required
            value={draft.energyCertificate}
            {...props('energyCertificate')}
            onChange={(event) => onChange('energyCertificate', event.target.value)}
          />
          <TextField
            label="Heizung"
            required
            value={draft.heating}
            {...props('heating')}
            onChange={(event) => onChange('heating', event.target.value)}
          />
          <TextField
            label="Bezugsfertig"
            required
            value={draft.readyForOccupancy}
            {...props('readyForOccupancy')}
            onChange={(event) => onChange('readyForOccupancy', event.target.value)}
          />
        </div>
      </div>

      <div className={dialogStyles.group}>
        <div className={dialogStyles.groupTitle}>Stellplätze</div>
        <div className={dialogStyles.grid2}>
          <TextField
            label="Anzahl"
            required
            inputMode="numeric"
            value={draft.parkingSpacesCount}
            error={fields['parkingSpacesCount']}
            {...props('parkingSpacesCount', '0, wenn das Objekt keine Stellplätze hat.')}
            onChange={(event) => onChange('parkingSpacesCount', event.target.value)}
          />
          <TextField
            label="Preis je Stellplatz"
            optional
            type="money"
            value={draft.parkingSpacePriceCents}
            error={fields['parkingSpacePriceCents']}
            {...props('parkingSpacePriceCents')}
            onChange={(event) => onChange('parkingSpacePriceCents', event.target.value)}
          />
        </div>
      </div>

      <div className={dialogStyles.group}>
        <div className={dialogStyles.groupTitle}>Kosten &amp; Recht</div>
        <div className={dialogStyles.grid2}>
          <TextField
            label="Hausgeld Ø je m² mtl."
            required
            type="money"
            value={draft.serviceChargePerSqmCents}
            error={fields['serviceChargePerSqmCents']}
            {...props('serviceChargePerSqmCents')}
            onChange={(event) => onChange('serviceChargePerSqmCents', event.target.value)}
          />
          <TextField
            label="Notar"
            required
            value={draft.notary}
            {...props('notary')}
            onChange={(event) => onChange('notary', event.target.value)}
          />
          <TextField
            label="Teilungserklärung vom"
            optional
            type="date"
            value={draft.declarationOfDivisionOn}
            error={fields['declarationOfDivisionOn']}
            {...props('declarationOfDivisionOn')}
            onChange={(event) => onChange('declarationOfDivisionOn', event.target.value)}
          />
          <TextField
            label="MEA gesamt"
            optional
            inputMode="decimal"
            value={draft.coOwnershipSharesTotal}
            error={fields['coOwnershipSharesTotal']}
            {...props('coOwnershipSharesTotal', 'Summe der Miteigentumsanteile laut Teilungserklärung')}
            onChange={(event) => onChange('coOwnershipSharesTotal', event.target.value)}
          />
          <Select
            label="Bundesland"
            required
            value={draft.state}
            placeholder="Automatisch nach PLZ"
            options={STATE_OPTIONS}
            error={fields['state']}
            hint={stateHint(record)}
            onChange={(event) => onChange('state', event.target.value as PropertyState | '')}
          />
          <TextField
            label="Grunderwerbsteuer"
            required
            inputMode="decimal"
            trailing={<span className={dialogStyles.suffix}>%</span>}
            value={draft.landTransferTaxPct}
            placeholder={formatNumber(automaticRate(record, draft.state), 2)}
            error={fields['landTransferTaxPct']}
            hint="Leer = automatisch nach Bundesland."
            onChange={(event) => onChange('landTransferTaxPct', event.target.value)}
          />
        </div>
      </div>

      <div className={dialogStyles.group}>
        <div className={dialogStyles.groupTitle}>Betreuung</div>
        <div className={dialogStyles.grid2}>
          <Select
            label="Objektbetreuung"
            required
            value={draft.managerUserId}
            placeholder="Keine Zuordnung"
            options={users.map((user) => ({ value: user.id, label: user.shortName }))}
            error={fields['managerUserId']}
            onChange={(event) => onChange('managerUserId', event.target.value)}
          />
          <TextField
            label="Externer Unterlagenordner"
            type="url"
            optional
            placeholder="https://"
            value={draft.documentsUrl}
            error={fields['documentsUrl']}
            onChange={(event) => onChange('documentsUrl', event.target.value)}
          />
        </div>
      </div>

      <CalcDefaultsFields
        title="Kalkulation"
        keys={CALC_BASE_DATA_KEYS}
        draft={calcDraft}
        fields={fields}
        org={org}
        required
        onChange={onCalcChange}
      />
    </div>
  );
}

export interface BaseDataSheetProps {
  open: boolean;
  record: PropertyRecord;
  onClose: () => void;
  /** After „Speichern" (`confirmed` false) or „Speichern und bestätigen" (`confirmed` true). */
  onSaved: (record: PropertyRecord, confirmed: boolean) => void;
}

/** „Stammdaten vervollständigen": the sheet with the base-data fields → `PATCH /properties/:id` (+ confirm). */
export function BaseDataSheet({ open, record, onClose, onSaved }: BaseDataSheetProps) {
  const [draft, setDraft] = useState<BaseDataDraft>(() => baseDataDraftOf(record));
  const [calcDraft, setCalcDraft] = useState<CalcDefaultsDraft>(() =>
    calcDefaultsDraftOf(record.calcDefaults),
  );
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'save' | 'confirm' | null>(null);
  const users = useKnownUsers(useMemo(() => (record.manager ? [record.manager] : []), [record.manager]));
  const external = record.external;
  const org = useOrganizationHints(open);

  useEffect(() => {
    if (open) {
      setDraft(baseDataDraftOf(record));
      setCalcDraft(calcDefaultsDraftOf(record.calcDefaults));
      setFields({});
      setError(null);
    }
  }, [open, record]);

  const complete = draftCompleteness(record, draft, calcDraft).status === 'complete';

  async function submit(confirm: boolean) {
    const { body, errors } = baseDataBody(record, draft, (field) => isManagedField(external, field));
    const calc = calcDefaultsBody(calcDraft, PROPERTY_CALC_KEYS);
    Object.assign(errors, calc.errors);
    if (Object.keys(errors).length > 0) {
      setFields(errors);
      return;
    }
    setFields({});
    setError(null);
    setPending(confirm ? 'confirm' : 'save');
    try {
      const saved = await updateProperty(record.id, {
        ...withoutManagedFields(body, external),
        calcDefaults: calc.body,
      });
      onSaved(confirm ? await confirmBaseData(record.id) : saved, confirm);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) setFields(err.fields);
      else setError(errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Stammdaten"
      subtitle={record.name}
      height="full"
      className={styles.sheet}
      footer={
        <>
          <Button
            variant="secondary"
            loading={pending === 'save'}
            disabled={pending === 'confirm'}
            onClick={() => void submit(false)}
          >
            Speichern
          </Button>
          <Button
            loading={pending === 'confirm'}
            disabled={!complete || pending === 'save'}
            onClick={() => void submit(true)}
          >
            Speichern und bestätigen
          </Button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <BaseDataForm
        record={record}
        draft={draft}
        calcDraft={calcDraft}
        fields={fields}
        users={users}
        org={org}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        onCalcChange={(key, value) => setCalcDraft((current) => ({ ...current, [key]: value }))}
      />
    </Sheet>
  );
}
